/**
 * Model MetamapVerification
 *
 */
export type MetamapVerification = {
  id: string
  resource: string | null
  data: object | null
  lastStatusReceived: string | null
  userId: number | null
  createdAt: Date | null
  updatedAt: Date | null
}

/**
 * Model MetamapStatusUpdate
 *
 */
export type MetamapStatusUpdate = {
  id: string
  status: string | null
  createdAt: Date
  dataReceived: object
  verificationId: string
}

/**
 * Model DiscordAccount
 *
 */
export type DiscordAccount = {
  id: string
  username: string | null
  image: string | null
  email: string | null
  userId: number | null
  LEGACY_METAMAP_VERIFICATION_ID: string | null
}

/**
 * Model TwitterAccount
 *
 */
export type TwitterAccount = {
  id: string
  username: string | null
  image: string | null
  email: string | null
  userId: number | null
}

/**
 * Model GoogleAccount
 *
 */
export type GoogleAccount = {
  id: string
  username: string
  image: string
  email: string
  userId: number
}

/**
 * Model FacebookAccount
 *
 */
export type FacebookAccount = {
  id: string
  username: string
  image: string
  email: string
  userId: number
}

export type Level = {
  value: number,
  nextLevelScore: number,
  prevLevelScore: number,
  score: number,
}

export type TournamentStats = {
  tournamentsPlayed?: number;
  tournamentsWon?: number;
  tournamentsPlayedStreak?: number;
  tournamentsWonStreak?: number;
}

/**
 * Model User
 *
 */
export type User = {
  id: number
  email: string | null
  avatar: string | null
  name: string | null
  createdAt: Date | null
  updatedAt: Date | null
  level: Level | null
  country: string | null
  referrals: User[]
  authProviders: AuthProviderData[] | null
  discordId?: string | null
  gameCategories?: GameCategory[]
  opportunities?: JobOpportunity[]
  gamingPlatforms?: Platforms[]
  roles: string[]
  wallets: UserWallet[]
  reactorWallet?: string | null
  allWallets?: UserWalletEntry[]
  verified: boolean
  lastSpin: string
  canSpinRoulette: boolean
  unreadNotifications: number,
  secret: string,
  hasWallet: boolean,
  hasDepositWallet: boolean,
  verification: {
    verified: boolean,
    level: number,
    verifiedAt: Date,
    providers?: string[]
  },
  homeStoriesViewed: boolean
  treasuresGameViewed: boolean
  streakCoachmarkViewed: boolean
}

export type AuthProviderData = {
  id: number
  provider: 'discord' | 'google' | 'ggtech'
  internalId: string
  avatar: string
  email: string
  username: string
  createdAt: Date
  updatedAt: Date
}

/**
 * Model Game
 *
 */
export type Game = {
  id: number
  slug: string
  title: string
  description: string | null
  longDescription: string | null
  image: string | null
  banner: string | null
  screenshots: string[]
  createdAt: Date
  updatedAt: Date
  visible: boolean | null
  status: string | null
  blockchains: string[]
  platforms: string[]
  index: number
  categories?: GameCategory[]
  rating: number
  votes: number
  userRating: number
  link?: string | null
  linkText?: string | null
  content: GameContent[]
  socialLinks: SocialLinks[]
  token?: string | null
  hideTokenMetrics: boolean | null
  tos?: Tos
  newsroomDescription?: boolean | null
  newsroomDescriptionBody?: string | null
  newsroomDescriptionBodyEn?: string | null
  newsroomDescriptionBodyPt?: string | null
  guides?: any | null
  extraData?: {
    translated?: boolean
    [key: string]: any
  }
}

export type GameContent = {
  link: string
  thumbnail: string
  type: GameContentType
}

export enum GameContentType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO'
}

export type SocialLinks = {
  link: string
  type: SocialLinksType
}

export enum SocialLinksType {
  WEB = 'WEB',
  DISCORD = 'DISCORD',
  YOUTUBE = 'YOUTUBE',
  TWITTER = 'TWITTER',
  INSTAGRAM = 'INSTAGRAM',
  TWITCH = 'TWITCH'
}

/**
 * Model GameCategory
 *
 */
export type GameCategory = {
  id: number
  name: string
  icon?: string | null
  longName: string
  categoryType: string
  extraData?: any
}

/**
 * Model Opportunities
 *
 */
export type JobOpportunity = {
  id: number
  name: string
  description?: string
  extraData?: any
  image?: any
}

/**
 * Model Platforms
 *
 */
export type Platforms = {
  id: number
  name: string
  label?: string
  image?: string
  extraData?: any
}

/**
 * Model Tos
 *
 */
export type Tos = {
  id: string
  content: string
  createdAt: Date
  updatedAt: Date
  gameId: string
  userAgreement: UserAgreement
}

/**
 * Model UserAgreement
 *
 */
export type UserAgreement = {
  id: string
  createdAt: Date
  updatedAt: Date
  userAgrees: boolean
  userId: number
  tosId: string
}

/**
 * Model ScholarshipApplication
 *
 */
export type ScholarshipApplication = {
  id: number
  gameId: string
  game?: Game
  userId: number | null
  createdAt: Date
  credential: GameCredentials | null
  status: ApplicationStatus
  user: User | null
  LEGACY_DISCORD_USER_ID: string | null
}

/**
 * Model ApplicationStatusUpdate
 *
 */
export type ApplicationStatusUpdate = {
  id: string
  applicationId: number
  message: string | null
  status: ApplicationStatus
  createdAt: Date
  operatedByAdminId: number | null
  operation: string | null
}

/**
 * Model GameCredentials
 *
 */
export type GameCredentials = {
  id: string
  active: boolean
  username: string | null
  password: string | null
  walletAddress: string | null
  seedPhrase: string | null
  userRequired: Array<keyof GameCredentials>
  notShow: Array<keyof GameCredentials>
  blockchain: string | null
  personal: boolean | null
  gameId: string
  game?: Game
  email: string | null
  adminNotes: string | null
  userNotes: string | null
  createdAt: Date | null
  updatedAt: Date | null
  nftIdentifier: string | null
  built: boolean | null
  userInputsCompleted: boolean | null
  user?: User | null
  scholarship?: ScholarshipApplication | null
}

/**
 * Model GameCredentialsUpdate
 *
 */
export type GameCredentialsUpdate = {
  id: string
  data: object | null
  timestamp: Date | null
  operation: string | null
  credentialsId: string
  scholarId: number | null
  operatedByAdminId: number | null
}

/**
 * Enums
 */

// Based on
// https://github.com/microsoft/TypeScript/issues/3192#issuecomment-261720275

export const Role = {
  USER: 'USER',
  ADMIN: 'ADMIN',
  SCHOLARSHIPS_REQUESTS_REVIEWER:
    'SCHOLARSHIPS_REQUESTS_REVIEWER'
}

// export type Role = typeof Role[keyof typeof Role]

export const ApplicationStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  SUSPENDED: 'SUSPENDED',
  PENDING_NO_KYC_YET: 'PENDING_NO_KYC_YET'
}

export type ApplicationStatus =
  typeof ApplicationStatus[keyof typeof ApplicationStatus]

export type Quest = {
  id?: string
  title: string
  description: string
  score: any
  createdAt: Date
  updatedAt: Date
  owner?: {
    id: string
    name: string
    order: number
    isPartner: boolean
  }
  type: QUEST_TYPES
  trigger?: string
  dependsOn?: string[]
  recursive: boolean
  multiplier: any
  icon: string | null
  buttonText?: string
  banner?: string
  callback?: string
  status: QUEST_STATUS
  action?: string
  iteration: number
  parents?: Quest[]
  visible: boolean,
  secretWord?: string,
  secretWordInstructions?: string
  secretWordMaxAttempts: number
  secretWordAttempt: number
  order: number
  extraData: any,
  avialbleFrom?: Date,
  availableTo?: Date,
  blockchainNeeded?: boolean,
  network?: string
  token?: string
  tokenName?: string
  tokenId?: string
  contractAddress?: string
  neededAmount?: string
  // Reactor (vault) missions: USDC balance at the last successful completion
  // ("saldo anterior") and the absolute balance to reach (= lastBalance +
  // neededAmount for incrementar_saldo). Serialized by QuestSerializer.
  lastBalance?: number
  targetAmount?: number
  missing_amount?: string
  retryIn?: string
  howToGetTokenUrl?: string
  address?: string
  lastAttemptToComplete?: string
  lastCompletionDate?: string
  badge?: TokenData
  twitchId: string
  twitchUsername: string
  steamAppId?: number
  steamPlaytimeRequired?: number
  autocomplete?: boolean
  clawCoinsReward?: number
  treasureOpportunitiesReward?: number
}

export type QuestPostBody = {
  secret_word?: string,
  location?: string
}

export type AdminQuest = {
  id?: string
  description: string | null
  shortDescription: string | null
  image: string | null
  avatar?: string
  score: number
  type: QUEST_TYPES
  actionText: string | null
  action: string | null
  dependsOn?: string[]
  createdAt: Date
  updatedAt: Date
  isRecursive: boolean
  multiplier: number
  topic: string | null
  ownerId?: string | null
}

export type QuestEvent = string

export enum QUEST_STATUS {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CLAIMED = 'CLAIMED',
  DISABLED = 'DISABLED',
  EXHAUSTED = 'EXHAUSTED',
  VERIFYING = 'VERIFYING',
}

export enum QUEST_TYPES {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC',
  ONE_CLICK = 'ONE_CLICK',
  ADMIN_COMPLETION = 'ADMIN_COMPLETION',
  SECRET_WORD = 'SECRET_WORD',
  TWITTER = 'TWITTER',
  TWITTER_V2 = 'TWITTER_V2',
  STEAM = 'STEAM',
  REACTOR = 'REACTOR',
}

// Variantes de misiones Reactor (ahorro en el vault AAVE). La variante se
// define por el trigger, igual que las de Steam (quest.steam.wishlist, etc.).
export enum REACTOR_TRIGGERS {
  INCREMENTAR_SALDO = 'quest.reactor.incrementar_saldo',
  SALDO_META = 'quest.reactor.saldo_meta',
  EDAD_DEL_SALDO = 'quest.reactor.edad_del_saldo',
}

/**
 * Model userQuestss
 *
 */
export type userQuests = {
  id: string
  status: QUEST_STATUS
  iteration: number
  createdAt: Date
  updatedAt: Date
  quest: Quest | null
}

/**
 * Model QuestsOwners
 *
 */
export type QuestOwner = {
  id: string
  name: string
  site: string
  gameId: number
  game?: Game
  discordId: string
  discordUsername: string
  twitterId: string
  twitterUsername: string
  order: number
  quests?: Quest[]
  isPartner?: boolean
  urlPath?: string
  title?: string
  logoUrl?: string
  mainColor?: string
  darkMode?: boolean
  instructions?: string
  essentialQuests?: any
  extraData: any
  organizer: boolean
}
export type Owner = QuestOwner

/**
 * Model StoreItem
 *
 */
export type MarketPlaceItem = {
  id?: string
  assigned?: boolean
  name: string
  description: string  // might be used for reward claiming instructions
  slug: string
  type: string
  redeemableRegion: string
  category: string
  availableStock: number
  status: string
  level?: number
  image: string
  hasSecretWord?: boolean
  dueDate: string
  visible: boolean
  callback: string
  link?: string  // might be used for claiming the reward
  couponsAvailable?: number
  couponsTaken?: number
  totalCodes?: number
  userStock?: MarketPlaceStockItem[],
  extraData: {
    [key: string]: any,
    translated: boolean
  },
  internalId?: number
}

export type MarketPlaceCouponsFormData = {
  quantity: number
}

export enum MarketPlaceItemType {
  LIMITED = 'LIMITED',
  UNLIMITED = 'UNLIMITED',
  LOTTERY = 'LOTTERY'
}

export enum MarketPlaceItemStatus {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE'
}

/**
 * Model StoreStockItem
 *
 */
export type MarketPlaceStockItem = {
  item: MarketPlaceItem
  code: string
  assignedToId?: number
  coupon?: string,
  secretWord?: string
}

/**
 * Model Metas
 *
 */
export type MetaTags = {
  title: string
  description: string
  url: string
  image: string
  /** Puede faltar: parte del catalogo no tiene autor con nombre */
  author?: string
  /** ISO 8601, para `article:published_time` y `datePublished` */
  publishedTime?: string
  /** ISO 8601, para `article:modified_time` y `dateModified` */
  modifiedTime?: string
  /** Descripcion sin truncar, para el JSON-LD */
  fullDescription?: string
  tags?: string[]
}

export enum ContentType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  DOCUMENT = 'DOCUMENT',
  LINK = 'LINK',
}

export enum UserContentStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type Vote = {
  id?: string;
  userId: number;
  userContentId: string;
  value: number;
  createdAt?: Date;
}

export type UserContent = {
  id?: string;
  title: string;
  userId: number;
  user: {
    id: number;
    name?: string;
    avatar?: string;
  };
  gameId: number;
  game: {
    id: number;
    title: string;
    slug: string;
  }
  content: string;
  type: ContentType;
  description: string;
  myVote?: Vote | number;
  positiveVotes?: number;
  negativeVotes?: number;
  status?: UserContentStatus;
  statusChangedBy?: number;
  active?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum NotificationType {
  EMAIL = 'EMAIL',
  PUSH = 'PUSH',
  SMS = 'SMS',
  IN_APP = 'IN_APP',
}

export type Segment = {
  id?: string;
  name: string;
  description: string;
  devices?: Device[];
  createdAt?: Date;
}

export type Device = {
  id?: string;
  uid?: string;
  token: string;
  userId: number;
  platform: string;
  browser?: string;
  os?: string;
  deviceName?: string;
  displayName?: string;
  lastUsedAt?: Date;
  active?: boolean;
  segments?: Segment[];
  createdAt?: Date;
  notificationPermissionStatus?: 'default' | 'granted' | 'denied';
}

export type Notification = {
  id?: string;
  uid?: string;
  type: NotificationType;
  image: string;
  title: string;
  body: string;
  link?: string;
  linkLabel?: string;
  devices?: Device[];
  segmentId?: string;
  segment?: {
    id: string;
    name: string;
  };
  sent?: boolean;
  createdAt?: Date;
  readAt?: Date;
  seenAt?: Date;
  userIds?: number[];
  userId?: number;
  extraData?: object;
  expiredAt?: Date;
}

export type UserWallet = {
  id: number
  network: string
  address: string
  main: boolean
  userId: string
  type?: "deposit" | "general"
  createdAt: Date
  updatedAt: Date
}

export type UserWalletType = 'deposit' | 'badges' | 'reactor' | 'purchase' | 'web3'

export type UserWalletEntry = {
  address: string
  type: UserWalletType
  network: string | null
  main: boolean
  source: string | null
}

export type TokenData = {
  id: number
  address: string
  balance: number
  classification: TokenClassificationType
  enabledAt: Date
  gaslessRelayer?: string
  maxTokensPerUser?: number
  mint?: {
    owner: string
    site: string
    claimPhases: boolean
    snapshotRequired: boolean
    requirements: string
  },
  order?: number
  requiredLevel?: number
  section: string
  tokenId: string
  visible: boolean
  canBeMinted?: boolean
  type: string
  network: string
}

export type BatchCompleteQuest = {
  file: FileList
}

export type BatchPaymentsUpload = {
  file: FileList
}

export type MarketPlaceStockFile = {
  file: FileList
}

export type ProductsCodesBatchUpload = {
  file: File;
};

// Torneos //
export enum ExternalIdType {
  EMAIL = "email",
  ID = "id",
  DISCORD = "discord",
}

export type FilterTournamentsParams = {
  page: number,
  title?: string,
  externalSlug?: string;
  slug?: string;
  region?: string;
  tournamentType?: string;
  playerId?: number;
  isCanceled?: boolean;
  from?: Date;
  to?: Date;
  sortStartDate?: string;
}

export type CreateTournamentParams = {
  title: string;
  image: string;
  banner: string;
  gameId: number;
  organizerId: string;
  externalSlug?: string | null;
  region?: string | null;
  reward: string;
  startDate: Date;
  endDate?: Date | null;
  type?: string | null;
  instructions?: string | null;
  inscriptionBases: {
    min: number;
    max?: number | null;
    openAt: Date;
    closeAt?: Date | null;
    price?: string | null;
    link: string;
  };
  participationScore?: number | null;
  winnerScore?: number | null;
  resultsUrl?: string | null;
  requiredAuth: boolean;
  requiredDiscord: boolean;
  showInBanner: boolean;
  canceled?: boolean | null;
  winnerTokenId?: string | null;
  participationTokenId?: string | null;
};

export type UpdateTournamentData = CreateTournamentParams;

export type inscriptionBases = {
  min: number;
  max?: number;
  openAt: Date;
  closeAt: Date;
  price?: string;
  link: string;
}

export type CreateInscriptionData = {
  tournamentId: string;
  externalId: string;
  externalIdType: ExternalIdType;
};

export type InscriptionData = {
  id?: string;
  type?: 'Intention' | 'Participation';
  tournamentId: string;
  position?: number;
  winner?: boolean;
  userId: number;
  user: User;
  blockchainToken?: {
    id: number;
    name: string;
  };
  prize?: PrizeData;
  score?: number;
  createdAt?: Date;
  updatedAt?: Date;
};

export type PrizeData = {
  blockchainToken?: number;
  givenBy?: number;
  other?: string;
};

export type PlayerData = {
  id?: string;
  externalId: string;
  externalIdType: ExternalIdType;
  username?: string;
  image?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export type TournamentOrganizerData = {
  id: string;
  name: string;
  blackLogo: string
  whiteLogo: string
};

export type TournamentData = {
  id: string;
  title: string;
  image: string;
  banner: string;
  gameId: number;
  game?: Game
  slug: string;
  externalSlug?: string;
  region?: string;
  reward: string;
  startDate: Date;
  endDate?: Date;
  inscriptionBases: inscriptionBases;
  participationScore?: number;
  winnerScore?: number;
  instructions?: string;
  organizerId: string;
  organizer: TournamentOrganizerData;
  type?: string;
  resultsUrl?: string;
  requiredAuth: boolean;
  requiredDiscord: boolean;
  showInBanner: boolean;
  winners?: InscriptionData[];
  isRegistered?: boolean;
  isCanceled: boolean;
  matchCode?: string;
  matchInstructions?: string;
  matchStartDate: Date | null;
  discordChannel?: string;
  discordUrl?: string;
  discordNotification: boolean | null;
  status: string;
  winnerToken?: TokenData;
  participationToken?: TokenData;
  blockchainToken?: BlockchainToken;
  worldIdLevel?: number;
  requireWorldId?: boolean;
  buttonText: {
    beforeInscription: { text: string, link: string },
    betweenInscriptionAndTournament: { text: string, link: string },
    inTournament: { text: string, link: string },
    afterTournament: { text: string, link: string },
  };
  extraData: {
    translated: boolean,
    [key: string]: any
  }
};

export type GameWithTournament = {
  title: string
  slug: string
}

// Torneos - Fin //

export * from './newsroom';

export interface VoteData {
  id?: string;
  userId: number;
  newsId?: string;
  value: number;
  createdAt?: Date;
}

export type ListVotesResponse = {
  votes: Array<VoteData>;
  count: number;
  totalUpVotes: number;
  totalDownVotes: number;
}

/* Token */

export enum TokenUserIdType {
  ID = 'ID',
  DISCORD = 'DISCORD'
}

export enum TokenClassificationType {
  GAMING = 'GAMING',
  COMMUNITY = 'COMMUNITY',
  EDUCATION = 'EDUCATION',
  GOVERNANCE = 'GOVERNANCE',
}

export type RelevantAction = {
  id: number;
  name: string;
  statsLabel: string;
  actionLabel: string;
  buttonLabel: string;
  buttonLink: string;
  icon: string;
  score: number;
  maxReferrers: number;
  userScore: number;
  userPendingScore: number;
  createdAt: string;
  requiredUserScore: number;
  extraData?: object;
}

export type Alert = {
  id: number;
  title: string;
  message?: string | null;
  showInPath: string[];
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  image?: string | null;
  visible: boolean;
  closable: boolean;
  type: string;
  startDate: Date | null;
  expirationDate: Date;
}

export type Role = {
  id: number;
  name: string;
  description?: string;
}

export type RolePermission = {
  id: number;
  role: Role,
  moduleName: string,
  permission: PermissionEnum,
};

export type CreateRolePermission = {
  role_id: number;
  moduleName: string,
  permission: PermissionEnum;
}

export type TokenDrop = {
  slug: string;
  id: string;
  releaseDate: Date;
  dueDate?: Date;
  quantity: number;
  minimumXp?: number;
  xpClaimedFrom?: Date;
  extraBonus?: number;
  maxTokensPerUser?: number;
  title: string;
  instructions: string;
}

export type CreateTokenDrop = {
  releaseDate: Date;
  dueDate: Date;
  quantity: number;
  minimumXp: number;
  xpClaimedFrom: Date;
  extraBonus: number;
  maxTokensPerUser: number;
  title: string;
  instructions: string;
}

export type StoryCategory = {
  id: number;
  name: string;
  fixed: boolean;
  thumbnail: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  icon: string | null;
  extraData: {
    en: {
      name: string;
    },
    pt: {
      name: string;
    }
  };
  availableStories: Story[];
}

export type Story = {
  id: number;
  title: string;
  createdAt: string;
  contentLink: string;
  cta: {
    text: string;
    link: string;
  } | null;
  duration: number;
  author: {
    id: number;
    name: string;
  };
  dueAt: Date | null;
  isImage: boolean;
  category: {
    id: number;
    name: string;
  };
  seen: boolean;
  totalViews: number;
  description: string;
  extraData: {
    en: {
      title: string;
      cta: {
        text: string;
      },
      description: string;
    },
    pt: {
      title: string;
      cta: {
        text: string;
      },
      description: string;
    },
  }
}

export type BlockchainNetwork = {
  id: number;
  name: string;
  chainId: number;
  nativeCurrencySymbol: string;
  networkIconUrl: string;
  blockExplorers: Array<string>;
  faucets?: Array<string>;
  rpcUrl?: string;
  isTestnet: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
  decimals?: number
}

export type BlockchainToken = {
  id: number;
  name: string;
  contractAddress: string;
  airdropContractAddress: string;
  tokenType: string;
  tokenIconUrl: string;
  tokenId?: number;
  network: BlockchainNetwork;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export type Payment = {
  id: number;
  userId: number;
  token: string;
  quantity: number;
  network: string;
  budget: string;
  description: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  address: string;
  link: string;
  status: string;
  reason: string;
  parentId: number;
  nonce: string;
  blockchainToken: BlockchainToken;
  creator: {
    id: number;
    name: string;
    email: string;
  }
}

export type Order = {
  id: string;
  status: string;
  amount: number;
  currency: string;
  decimals: number;
  code: string | null;
  createdAt: string;
  updatedAt: string;
  txHash: string | null;
  origin?: string | null;
  user: User;
  product: Product;
  totalWithDiscount?: number;
  inventorySource?: InventorySource;
  inventoryCode?: {
    id: number;
    code: string;
    cost: string | null;
    expiresAt: string | null;
    deliveredAt: string | null;
  } | null;
  transactions: Array<{
    id: number;
    orderId: string;
    txId: string | null;
    status: string;
    substatus: string;
    amount: number;
    currency: string;
    decimals: number;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    rawData: any;
  }>;
}

export type Bonox = {
  id: number;
  name: string;
  redeemable_region: string;
  face_value: string;
  selling_price: number;
  selling_currency: string;
  merchant_price: number;
  merchant_currency: string;
  commission: string;
  in_stock: boolean;
}

export type ProductSource = 'BonoxProduct' | 'LocalProduct';
export type InventorySource = 'BONOXS' | 'OLAGG';

export type Product = {
  id: string;
  name: string;
  displayName: string;
  slug: string;
  redeemableRegion: string;
  faceValue: string;
  sellingPrice: number;
  sellingCurrency: string;
  merchantPrice: number;
  merchantCurrency: string;
  commission: string;
  markupPercentage: string;
  inStock: boolean;
  visible: boolean;
  source: ProductSource | string;
  referenceId: number;
  image: string;
  link: string | null;
  linkText: string | null;
  description: string;
  instructions: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  related?: Product[];
  category: string;
  requiredLevel?: number;
  order: number;
  inventorySource?: InventorySource;
  inventoryAvailableCount?: number;
  inventoryTotalCount?: number;
  extraData?: {
    [key: string]: any;
  };
}

export type InventoryCodeStatus = 'available' | 'delivered' | 'expired';

export type InventoryCode = {
  id: number;
  code: string;
  expiresAt: string | null;
  cost: string | null;
  deliveredAt: string | null;
  createdAt: string;
  orderId: number | null;
  status: InventoryCodeStatus;
  user: { id: number; email: string; name: string } | null;
}

export type InventoryCodeUploadSkip = {
  row: number;
  code: string | null;
  reason: 'duplicate_in_file' | 'duplicate_in_db' | 'invalid_expires_at' | 'invalid_cost' | 'missing_code' | string;
}

export type InventoryCodesUploadResponse = {
  imported: number;
  skipped: InventoryCodeUploadSkip[];
  availableCount?: number;
  totalCount?: number;
  available_count?: number;
  total_count?: number;
}

export type InventoryCodesListResponse = {
  inventory_codes: InventoryCode[];
  meta: {
    count: number;
    available_count: number;
    total_count: number;
  }
}

export type InventoryCodesFilter = {
  page?: number;
  limit?: number;
  status?: InventoryCodeStatus | '';
  q?: string;
}

export type CreateProductParams = {
  name: string;
  display_name: string;
  slug?: string;
  redeemable_region: string;
  face_value: string;
  selling_price: number;
  selling_currency: string;
  merchant_price: number;
  merchant_currency: string;
  commission: string;
  markup_percentage: string;
  in_stock: boolean;
  visible: boolean;
  source: string;
  reference_id: number;
  image: string;
  description: string;
  instructions: string;
  category: string;
  extra_data?: {
    [key: string]: any;
  };
}

export type UpdateProductParams = CreateProductParams;

export enum PermissionEnum {
  NONE = 0,
  VIEW = 1,
  EDIT = 2,
  CREATE = 3,
  DELETE = 4,
  ALL = 10
}

export enum OlaggModule {
  Game = 'Juegos',
  Scholarship = 'Solicitudes',
  Credential = 'Becas',
  User = 'Usuarios',
  Owner = 'Owners',
  Quest = 'Misiones',
  RelevantAction = 'Acciones relevantes',
  Notification = 'Notificaciones',
  UserContent = 'UGC',
  Item = 'Tienda',
  Tournament = 'Torneos',
  Inscription = 'Torneos (Participantes)',
  Token = 'Tokens',
  TokenAssignment = 'Tokens (Whitelist)',
  Alert = 'Alertas',
  Role = 'Permisos',
  RouletteValue = 'Ruleta',
  TokenDrop = 'Token drops',
  News = 'Novedades',
  Payment = 'Pagos',
  TreasuresGame = 'Mini Juegos',
  Survey = 'Encuestas',
  GameCategory = 'Preferencias del Usuario (Géneros de Juegos)',
  Opportunity = 'Preferencias del Usuario (Oportunidades de interés)',
  GamingPlatform = 'Preferencias del Usuario (Plataformas)',
  Story = 'Historias',
  StoryCategory = 'Categorías de Historias',
  BlockchainToken = 'Catálogo de Blockchain Tokens',
  BlockchainTokenNetwork = 'Networks de Blockchain Tokens',
  Streak = 'Rachas',
  CardDeck = 'Cards',
  Product = 'Productos',
  Order = 'Ordenes',
  Event = 'Eventos',
  ProductCode = 'Cupones',
  Season = 'Temporadas',
  ClawMachinePrize = 'Premios Claw Machine',
  LevelReward = 'Premios por Nivel',
  PurchaseRewardPromotion = 'Promociones de Compra',
  PurchaseRewardExclusion = 'Exclusiones de Recompensa',
  Trivia = 'Trivia',
  ReactorEconomyConfig = 'Reactor (Producción de Créditos)',
  ArticleCta = 'CTAs de Novedades',
  ArticlePoll = 'Encuestas de Novedades',
  /*
  * ^^ (agregar aca arriba) ^^
  *
  * <Nombre del modulo> = <Texto a mostrar en el dropdown de permisos>
  *
  * IMPORTANTE: El nombre del modulo tiene que ser igual a como esta definido el modelo en el backend
  *
  */
}

// Reactor credit-production economy parameters (backend: reactor_economy_configs).
// Numeric fields are NUMERIC in Postgres and may serialize as strings (BigDecimal),
// so consumers should coerce with Number() before formatting/arithmetic.
export type ReactorEconomyConfig = {
  id: number,
  rMin: number | string,   // floor rate (credits/hour), e.g. 1/168 = 1 credit/week
  rMax: number | string,   // cap rate (credits/hour), e.g. 1 = 1 credit/hour
  dCap: number | string,   // USDC balance at which the rate reaches the cap
  welcomeAmount: number | string, // one-time welcome bonus granted per user
  effectiveFrom: string,
  createdAt: string,
}

export type RouletteValue = {
  id: number,
  position: number,
  value: number,
  probability: number,
}

// Formato de respuesta del multi-search en /novedades
// -> unifica Novedades de NR, Guias de NR y Videos de Ola (UGC)
export type NewsMultisearchType = {
  id: number,
  uid: string,
  title: string,
  title_en: string,
  title_pt: string,
  description: string,
  description_en: string,
  description_pt: string,
  image: string,
  image_en: string,
  image_pt: string,
  content_type: string,
  total_views: number,
  author_id: number,
  publication_date: string,
  author_name?: string,
  author_avatar?: string,
  author_old_avatar_url?: string,
  up_votes: number,
  down_votes: number,
  author_role?: string,
  ai_generated?: boolean
};

export type TreasuresGame = {
  id: number;
  title: string;
  instructions: string;
  slug: string;
  startTime: string;
  endTime: string;
  autoRestart: boolean;
  visible: boolean;
  coverImageUrl?: string;
  mapImageUrl?: string;
  tilesImageUrl?: string;
  rows: number;
  columns: number;
  totalPrize: {
    token: string;
    amount?: string;
    network?: number;
    tokenType?: string;
    contractAddress?: string;
  };
  blockchainTokenId?: number;
  totalTreasures: number;
  waitTime: number;
  minXpThreshold: number;
  requireXpOnFirstMovement: boolean;
  minLevel: number;
  maxLevel: number | null;
  ownerId: string;
  ownerName: string;
  ownerLogo: string;
  status: 'upcoming' | 'active' | 'finished';
  extraData?: object;
  betaTesterIds?: number[];
  duplicated?: string;
  lead?: string;
}

export type TreasuresGameMovement = {
  id: number;
  userId: number;
  xpAccumulated: number;
  row: number;
  column: number;
  foundTreasure: boolean;
  userName: string;
  userAvatar: string;
}

export type TreasuresGameGridStatus = {
  rows: number;
  columns: number;
  grid: number[][];
  revealed_count: number;
  discovered_count: number;
  total_tiles: number;
  last_update: number;
  players_count: number;
}

export type TreasuresGamePlayerStatus = {
  level_too_low: boolean;
  level_too_high: boolean;
  wait_seconds: number;
  required_xp: number;
  extra_opportunities: number;
}

export type TreasuresGamePlayResult = {
  message: string;
  found_treasure: boolean;
  consolation_prize: number;
  payment: string;
  payment_error: string;
}

export type AdminTreasureGameOpportunity = {
  id: number;
  active: boolean;
  usedAt: Date;
  expiresAt?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  userInfo: {
    id: number;
    name: string;
    email: string;
  };
  expired: boolean;
  used: boolean;
  available: boolean;
}

export type TreasuresGameWinnerInfo = {
  user_id: number;
  user_name: string;
  user_avatar: string;
  prize_amount: number;
  prize_token: string;
  created_at: string;
}

export type Streaks = {

}

export * from './card-decks';


type EventStatus = 'published' | 'canceled' | 'draft';

export type EventCoverImage = {
  en?: string;
  es?: string;
  pt?: string;
}

export type Host = {
  id: number;
  name: string;
  avatar: string;
  profileLink: string;
}

export type Event = {
  uid: string;
  newsroomId: string;
  title: {
    es: string;
    en: string;
    pt: string;
  };
  game?: Game;
  description: {
    es: string;
    en: string;
    pt: string;
  };
  prize: {};
  slug: string;
  coverImage: EventCoverImage;
  coverImageBasePath: string;
  letUserKnow: boolean;
  startsAt: Date;
  endsAt: Date;
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
  host: Host;
  streamingLink: string;
  streamingPlatform: string;
  usersClickedLetMeKnow: {
    count: number;
    users: User[];
  };
  link: string;
  live: boolean;
  status: EventStatus;
  extraData: object;
  eventType: 'regular' | 'tournament' | 'recurrent';
  rescheduled: {
    newStartsAt?: Date;
    oldStartsAt?: Date;
  },
  location?: string;
  regional?: boolean;
  cta?: {
    es?: {
      text: string, link: string
    },
    en?: {
      text: string, link: string
    },
    pt?: {
      text: string, link: string
    }
  }
}

export type AdminSeason = {
  id: number;
  title: string;
  slug: string;
  startAt: Date;
  endAt: Date;
  visible: boolean;
  status: 'active' | 'upcoming' | 'finished';
  image?: string;
  description?: string;
  totalDays: number;
  challengesCount: number;
  clawMachinePrizesCount: number;
  extraData?: {
    en?: {
      title?: string;
      description?: string;
      image?: string;
    };
    pt?: {
      title?: string;
      description?: string;
      image?: string;
    };
  };
  createdAt: Date;
  updatedAt: Date;
}

export type AdminSeasonChallenge = {
  id: number;
  title: string;
  description?: string;
  dayNumber: number;
  clawCoinsReward: number;
  extraData?: {
    en?: {
      title?: string;
      description?: string;
    },
    pt?: {
      title?: string;
      description?: string;
    }
  };
  gameSeasonChallengeTasksCount: number;
  gameSeasonId: number;
  createdAt: Date;
  updatedAt: Date;
}

export type AdminSeasonChallengeTask = {
  id: number;
  title: string;
  actionType: 'quest' | 'tournament_participation' | 'tournament_win' | 'survey' | 'treasure_play' | 'treasure_win' | 'streak' | 'news_vote' | 'game_rating' | 'referral' | 'badge_claim' | 'event_participation' | 'shop_purchase';
  actionParams: {
    [key: string]: any;
  };
  gameSeasonChallengeId: number;
  targetType?: string;
  targetId?: number;
  targetInfo?: {
    [key: string]: any;
  };
  extraData?: {
    en?: {
      title?: string;
      description?: string;
    };
    pt?: {
      title?: string;
      description?: string;
    };
  };
  createdAt: Date;
  updatedAt: Date;
}

export type AdminSeasonClawMachinePrize = {
  id: number;
  uid: string;
  title: string;
  imageUrl?: string;
  gameSeasonId: number;
  clawMachinePrizeId?: number;
  active: boolean;
  stockType: 'limited' | 'unlimited';
  rewardType: 'xp' | 'treasure_chance' | 'claw_coin' | 'token' | 'blockchain_token' | 'store_product' | 'store_discount' | 'physical_item';
  rewardValue?: number;
  probability?: number;
  cheatCodeProbability?: number;
  cheatCodePositions?: number[];
  quantity?: number;
  budget_allocated?: number;
  createdAt: Date;
  updatedAt: Date;
}

export type AdminClawMachinePrize = {
  id: number;
  title: string;
  description?: string;
  imageUrl?: string;
  rewardableType: string;
  rewardableId: number;
  rewardableInfo?: {
    [key: string]: any;
  };
  rewardType?: 'xp' | 'treasure_chance' | 'claw_coin' | 'token' | 'blockchain_token' | 'store_product' | 'store_discount' | 'physical_item';
  extraData?: {
    en?: {
      title?: string;
      description?: string;
      imageUrl?: string;
    };
    pt?: {
      title?: string;
      description?: string;
      imageUrl?: string;
    };
  };
  createdAt: Date;
  updatedAt: Date;
}

export type TreasureGameOpportunity = {
  id: number;
  active: boolean;
  usedAt: Date;
  expiresAt?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  userInfo: {
    id: number;
    name: string;
    email: string;
  };
  expired: boolean;
  used: boolean;
  available: boolean;
}

export type GameSeason = {
  id: number;
  title: string;
  description: string;
  startAt: Date;
  endAt: Date;
  status: 'active' | 'upcoming' | 'finished';
  currentDayNumber: number;
  totalDays: number;
  daysRemaining: number;
  progressPercentage: number;
  image: string | null;
  createdAt: Date;
  updatedAt: Date;
  extraData: {},
  availableClawMachineCoins: number,
  totalClawMachinePrizes: number,
  availableClawMachinePrizes: number
}

export type GameSeasonChallengeTask = {
  id: number,
  title: string,
  completionStatus: {
    completed: false,
    completedAt: Date | null
  },
  createdAt: Date,
  updatedAt: Date,
  link: string | null
}

export type GameSeasonChallenge = {
  id: number,
  title: string,
  description: string,
  dayNumber: number,
  clawCoinsReward: number,
  startAt: Date,
  endAt: Date,
  active: boolean,
  expired: boolean,
  completedByUser: boolean,
  canBeCompletedByUser: boolean,
  completionStatus: {
    completed: false,
    progress: {
      total: number,
      completed: number,
      percentage: number
    },
    completedAt: Date | null
  },
  gameSeasonChallengeTasks: GameSeasonChallengeTask[],
  imageUrl: string | null,
  createdAt: Date,
  updatedAt: Date,
  gameSeason: GameSeason,
  clawMachinePrizes: AdminSeasonClawMachinePrize[]
}

export type GameSeasonClawMachinePrize = {
  title: string,
  description: string | null,
  imageUrl: string | null,
  extraData: object | null,
  totalClaimed: number,
  remainingQuantity: number,
  outOfStock: boolean,
  rewardValue: string
}

export type GameSeasonClawMachinePlayPrize = {
  title: string,
  description: string,
  image_url: string,
  extra_data: object | null,
}

export type AdminGameSeasonClawMachinePrizeClaim = {
  id: number;
  claimedAt: Date;
  createdAt: Date;
  userInfo: {
    id: number;
    username: string;
    email: string;
  };
  playInfo: {
    id: number;
    xPosition: number;
    zPosition: number;
    cheatCodeUsed: boolean;
    playedAt: Date;
  }
}

export type AdminClawMachineCoin = {
  id: number;
  userId: number;
  gameSeasonId: number;
  usedAt: Date | null;
  createdAt: Date;
  userInfo: {
    id: number;
    name: string;
    email: string;
  },
  gameSeasonInfo: {
    id: number;
    title: string;
    status: string;
  }
}

export type ClawMachineCoin = {
  id: number;
  sourceInfo: {
    type: string;
    id: number;
    title: {
      [key: string]: any
    };
  };
  expired: boolean;
  used: boolean;
  expiresAt: Date | null;
  createdAt: Date;
  usedAt: Date | null;
}

export type AdminLevelReward = {
  id: number;
  title: string;
  level: number;
  rewardType: 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward';
  rewardConfig: { [key: string]: any };
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  priority: number;
  extraData: { [key: string]: any };
}

export type AdminLevelRewardAssignment = {
  id: number;
  userId: number;
  levelRewardId: number;
  status: 'pending' | 'completed' | 'failed';
  assignmentData: { [key: string]: any };
  errorMessage: string | null;
  assignedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  userInfo: {
    id: number;
    email: string;
    name: string;
  };
  rewardInfo: {
    id: number;
    level: number;
    rewardType: 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward';
    rewardTitle: string;
  }
}

export type PurchaseRewardPromotionScope = 'all_products' | 'category' | 'specific_products';

export type PurchaseRewardPromotion = {
  id: number;
  uid: string;
  name: string;
  description?: string;
  scope: PurchaseRewardPromotionScope;
  category?: string;
  fixedXp: number;
  fixedTreasureOpportunities: number;
  fixedClawCoins: number;
  xpPerUsdc: number;
  treasurePerUsdc: number;
  clawCoinsPerUsdc: number;
  coinExpirationDays?: number;
  treasureOpportunityExpirationDays?: number;
  startsAt?: Date;
  endsAt?: Date;
  active: boolean;
  maxUsesPerUser?: number;
  maxTotalUses?: number;
  currentTotalUses: number;
  products?: {
    id: number;
    uid: string;
    name: string;
    category: string;
  }[];
  claimsCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export type PurchaseRewardExclusion = {
  id: number;
  reason?: string;
  product: {
    id: number;
    uid: string;
    name: string;
    category: string;
  };
  createdAt: Date;
}

// Trivia Types
export type TriviaAnswerTranslation = {
  text: string;
}

export type TriviaAnswerExtraData = {
  en?: TriviaAnswerTranslation;
  [key: string]: TriviaAnswerTranslation | undefined;
}

export type TriviaAnswer = {
  id?: number;
  uid?: string;
  text: string;
  isCorrect: boolean;
  position: number;
  extraData?: TriviaAnswerExtraData;
  _destroy?: boolean;
}

export type TriviaQuestionTranslation = {
  title?: string;
  text?: string;
  explanation?: string;
}

export type TriviaQuestionExtraData = {
  en?: TriviaQuestionTranslation;
  pt?: TriviaQuestionTranslation;
  [key: string]: TriviaQuestionTranslation | undefined;
}

export type TriviaQuestion = {
  id: number;
  uid: string;
  title?: string;
  text: string;
  explanation?: string;
  points: number;
  date: string;
  position: number;
  timeLimitSeconds: number | null;
  active: boolean;
  speedBonusEnabled: boolean;
  speedBonusMultiplier: string;
  extraData: TriviaQuestionExtraData;
  answersCount: number;
  responsesCount: number;
  createdAt: string;
  updatedAt: string;
  triviaAnswers: TriviaAnswer[];
}

export type TriviaCalendarDay = {
  questions_count: number;
  total_points: number;
  has_speed_bonus: boolean;
  has_active_questions: boolean;
}

export type TriviaCalendar = {
  [date: string]: TriviaCalendarDay;
}

// User-facing Trivia Types
export type TriviaStatus = {
  available: boolean;
  reason: 'already_completed' | 'no_questions_today' | null;
  message: string | null;
  progress: {
    total: number;
    answered: number;
    remaining: number;
  } | null;
}

export type TriviaUserAnswer = {
  answer_id: string;
  correct: boolean;
  base_points: number;
  speed_bonus_points: number;
  points_earned: number;
}

export type TriviaUserQuestionAnswer = {
  id: number;
  uid: string;
  text: string;
  position: number;
  extraData?: TriviaAnswerExtraData;
  extra_data?: TriviaAnswerExtraData;
}

export type TriviaUserQuestion = {
  id: number;
  uid: string;
  title: string;
  text: string;
  points: number;
  date: string;
  position: number;
  // Support both camelCase (API) and snake_case
  timeLimitSeconds?: number | null;
  time_limit_seconds?: number | null;
  speedBonusEnabled?: boolean;
  speed_bonus_enabled?: boolean;
  speedBonusMultiplier?: string | null;
  speed_bonus_multiplier?: string | null;
  answered: boolean;
  userAnswer?: TriviaUserAnswer | null;
  user_answer?: TriviaUserAnswer | null;
  explanation?: string | null;
  extraData?: TriviaQuestionExtraData;
  extra_data?: TriviaQuestionExtraData;
  // Support both naming conventions
  answers?: TriviaUserQuestionAnswer[];
  triviaAnswers?: TriviaUserQuestionAnswer[];
  trivia_answers?: TriviaUserQuestionAnswer[];
}

export type TriviaTodayResponse = {
  // Support both v1 and v2 API response formats
  data?: TriviaUserQuestion[];
  triviaQuestions?: TriviaUserQuestion[];
  triviaDailyAssignments?: TriviaUserQuestion[];
  meta: {
    date: string;
    completed: boolean;
    // Support both camelCase and snake_case
    totalQuestions?: number;
    total_questions?: number;
    answeredQuestions?: number;
    answered_questions?: number;
    remainingQuestions?: number;
    remaining_questions?: number;
  };
}

export type TriviaDaySummary = {
  total_points: number;
  correct_answers: number;
  total_questions: number;
  percentage: number;
}

export type TriviaExplanation = {
  text: string;
  extra_data?: Record<string, string>;
  extraData?: Record<string, string>;
}

export type TriviaRespondResponse = {
  correct: boolean;
  base_points: number;
  speed_bonus_points: number;
  points_earned: number;
  correct_answer_id: string;
  explanation?: TriviaExplanation | null;
  day_completed: boolean;
  day_summary: TriviaDaySummary | null;
}

export type TriviaLeaderboardEntry = {
  uid: string;
  score: number;
  // Support both camelCase (new API) and snake_case
  display_name?: string;
  displayName?: string;
  avatar_url?: string | null;
  avatarUrl?: string | null;
  position?: number;
  weekId?: string;
  lastDayKey?: string;
  updatedAt?: string;
  _name?: string;
}

export type TriviaLeaderboardResponse = {
  type: 'weekly' | 'global';
  week_id?: string;
  entries: TriviaLeaderboardEntry[];
  user_position: number | null;
  user_entry: TriviaLeaderboardEntry | null;
  source: string;
  pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_entries: number;
  };
}

export type TriviaHistoryEntry = {
  id: number;
  day_key: string;
  week_id: string;
  completed_at: string;
  total_points: number;
  correct_answers: number;
  total_questions: number;
  percentage: number;
}

export type TriviaHistoryStats = {
  total_completions: number;
  total_points_earned: number;
  average_score: number;
  best_score: number;
  current_streak: number;
}

export type TriviaHistoryResponse = {
  data: TriviaHistoryEntry[];
  meta: {
    total_count: number;
    page: number;
    per_page: number;
    total_pages: number;
    stats: TriviaHistoryStats;
  };
}

export type TriviaUserStats = {
  total_points: number;
  total_games: number;
  current_streak: number;
  best_streak: number;
  current_week_points: number;
  current_week_id: string;
  total_correct_answers: number;
  total_questions_answered: number;
  average_percentage: number;
}

// Admin Trivia v2 Types - Templates (Catálogo de Preguntas)
export type TriviaTemplateAnswer = {
  id: number;
  uid: string;
  text: string;
  isCorrect: boolean;
  position: number;
  extraData?: Record<string, any>;
}

export type TriviaQuestionTemplate = {
  id: number;
  uid: string;
  title: string;
  text: string;
  explanation?: string;
  points: number;
  timeLimitSeconds: number | null;
  speedBonusEnabled: boolean;
  speedBonusMultiplier: string;
  extraData?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  answersCount: number;
  usageCount: number;
  assignedDates: string[];
  triviaTemplateAnswers: TriviaTemplateAnswer[];
}

export type TriviaTemplatesResponse = {
  triviaQuestionTemplates: TriviaQuestionTemplate[];
  meta: {
    count: number;
  };
}

// Admin Trivia v2 Types - Assignments (Calendario)
export type TriviaDailyAssignment = {
  id: string;
  template_id: string;
  title: string;
  position: number;
  points: number;
  active: boolean;
}

export type TriviaCalendarDay = {
  questions_count: number;
  total_points: number;
  has_speed_bonus: boolean;
  has_active_questions: boolean;
  assignments: TriviaDailyAssignment[];
}

export type TriviaCalendarResponse = {
  calendar: {
    [date: string]: TriviaCalendarDay;
  };
  start_date: string;
  end_date: string;
}

/**
 * Inventario de CTAs de la pagina de articulo (requerimientos R1 a R11).
 */

export const ARTICLE_CTA_SLOTS = [
  'right_rail',
  'in_body_mid',
  'sticky_mobile',
  'in_body_top',
  'end_of_article',
] as const;

export type ArticleCtaSlot = (typeof ARTICLE_CTA_SLOTS)[number];

export type ArticleCtaRenderType = 'standard' | 'poll';

/**
 * Tabla de rendicion por slot (R8). Es la fuente de verdad de la geometria con
 * la que se pide y se declara la imagen, y la comparten el `<img>` del
 * componente y la preview de team.
 *
 * `width`/`height` son los px CSS a los que se sirve la imagen: el `<img>` los
 * declara y son ciertos para cualquier fuente, porque se pide recortada
 * (`fit=cover`) a esa caja. Eso es lo que elimina el CLS.
 *
 * `minSourceWidth` es 2x la rendicion porque el helper del CDN pide `dpr`: en
 * pantallas 2x va a pedir el doble. Por debajo de 1x se bloquea la carga del
 * arte (Cloudflare no agranda), entre 1x y 2x se avisa (R6.1).
 */
export const ARTICLE_CTA_RENDITIONS: Record<
  ArticleCtaSlot,
  { width: number; height: number; minSourceWidth: number; minSourceHeight: number }
> = {
  right_rail: { width: 360, height: 270, minSourceWidth: 720, minSourceHeight: 540 },
  in_body_mid: { width: 720, height: 405, minSourceWidth: 1440, minSourceHeight: 810 },
  in_body_top: { width: 720, height: 405, minSourceWidth: 1440, minSourceHeight: 810 },
  end_of_article: { width: 720, height: 405, minSourceWidth: 1440, minSourceHeight: 810 },
  // La barra inferior no elige su rendicion como los primarios: la deduce de su
  // alto maximo (ver ARTICLE_CTA_STICKY_MAX_HEIGHT). 48 es el arte mas grande
  // que entra en el presupuesto, asi que subirlo aca no agranda la barra, la
  // recorta. Para darle mas arte hay que subir primero el maximo.
  sticky_mobile: { width: 48, height: 48, minSourceWidth: 96, minSourceHeight: 96 },
};

/**
 * Alto maximo de la barra inferior movil, en px CSS.
 *
 * La barra es `position: fixed` sobre el articulo: cada px que mide es un px de
 * lectura tapado, y a diferencia de un primario el lector no puede scrollearla
 * fuera de vista. Con la rendicion en 96 la barra medi­a 121 px (96 de arte + 24
 * de padding + 1 de borde), casi un quinto de un viewport de 667: mas que el
 * `pb` de 80 px con el que el articulo se separa del fondo, asi que la barra
 * tapaba los ultimos 41 px del cuerpo.
 *
 * El maximo es el numero normativo y la rendicion se deriva de el, no al revés:
 *
 *     arte <= maximo - padding vertical - borde  ->  48 <= 80 - 24 - 1
 *
 * El componente lo aplica como `maxH` real con `overflow: hidden`, asi que es
 * un tope y no una sugerencia. Con el arte en 48 la altura la termina fijando
 * el texto (titulo de 2 lineas + subtitulo = 51 px), y la barra queda en ~76 px:
 * abajo del tope y abajo del `pb` del articulo, que ahora si la despeja.
 */
export const ARTICLE_CTA_STICKY_MAX_HEIGHT = 80;

/** Slots primarios y persistentes (R7): son de tipo distinto y no compiten. */
export const ARTICLE_CTA_PRIMARY_SLOTS: ArticleCtaSlot[] = [
  'right_rail',
  'in_body_mid',
  'in_body_top',
  'end_of_article',
];

export const ARTICLE_CTA_PERSISTENT_SLOTS: ArticleCtaSlot[] = ['sticky_mobile'];

export type ArticleCtaContent = {
  title?: string | null;
  subtitle?: string | null;
  ctaLabel?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
};

export type ArticleCtaTargeting = {
  tags?: string[] | null;
  lang?: string[] | null;
  device?: string[] | null;
  loggedIn?: boolean | null;
};

/** La fila del inventario, como la devuelven los endpoints de admin. */
export type ArticleCtaType = {
  id: number;
  uid: string;
  key: string;
  slot: ArticleCtaSlot;
  renderType: ArticleCtaRenderType;
  variant: string;
  experimentKey?: string | null;
  weight: number;
  active: boolean;
  content: Record<'es' | 'en' | 'pt', ArticleCtaContent>;
  targeting: ArticleCtaTargeting;
  destination: string;
  /**
   * Dimensiones del ARCHIVO fuente, medidas por el formulario (R6.1). No son la
   * geometria del `<img>`: esa sale de ARTICLE_CTA_RENDITIONS.
   */
  imageWidth?: number | null;
  imageHeight?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

/** Una opcion de encuesta, como viaja en la respuesta publica (R21). */
export type ArticlePollOption = {
  id: number;
  label: string;
  /** `code` para las extraidas del cuerpo, `none_worked` para la fija. */
  kind: 'code' | 'none_worked';
};

/**
 * La encuesta embutida en el candidato cuando `render_type = poll`. Viaja en la
 * misma respuesta que los CTAs para no romper el cache: depende solo de
 * (slug, lang, device, logged_in) y de nada del lector.
 *
 * `total_votes` puede estar hasta un ciclo de cache desactualizado y esta bien:
 * antes de votar no se muestran porcentajes.
 */
export type ArticleCtaPoll = {
  uid: string;
  question: string;
  options: ArticlePollOption[];
  total_votes: number;
};

/** Resultado por opcion, que el backend devuelve recien con el voto. */
export type ArticlePollResult = ArticlePollOption & {
  votes: number;
  percentage: number;
};

export type ArticlePollResults = {
  uid: string;
  total_votes: number;
  my_vote: number;
  results: ArticlePollResult[];
};

/**
 * Un candidato ya resuelto, como lo devuelve el endpoint publico (R3). Viene en
 * snake_case: la respuesta es un hash de slots armado a mano y no pasa por el
 * `camel_lower` del serializer.
 *
 * No trae `image_width`/`image_height` a proposito: devolverlas invita a
 * cablearlas al `<img>`, que es el error que produce CLS.
 */
export type ResolvedArticleCta = {
  key: string;
  render_type: ArticleCtaRenderType;
  variant: string;
  experiment_key: string | null;
  weight: number;
  title: string;
  subtitle?: string | null;
  cta_label: string;
  image_url?: string | null;
  image_alt?: string | null;
  destination: string;
  /** Solo en las filas con `render_type = poll`. */
  poll?: ArticleCtaPoll | null;
};

export type ArticleCtaSlots = Partial<Record<ArticleCtaSlot, ResolvedArticleCta[]>>;

/**
 * Un destino externo -- la landing de un partner, una waitlist -- se guarda con
 * el esquema y el backend lo sirve tal cual: sin prefijo de idioma y sin
 * nuestras UTMs, que pisarian las que trae la URL cargada. En la app abre en
 * pestania nueva, a diferencia de un destino interno.
 */
export const isExternalCtaDestination = (destination?: string | null) =>
  /^https?:\/\//i.test(destination || '');

/**
 * Una encuesta del articulo, como la devuelven los endpoints de admin (R23).
 *
 * No hay `create`: las encuestas las crea el extractor desde el cuerpo del
 * articulo. Desde team se revisan y se editan puntualmente, y cualquier edicion
 * pone `source = manual`, que congela la encuesta.
 */
export type ArticlePollOptionRow = {
  id: number;
  /** Las extraidas del cuerpo traen el codigo; las fijas traen `kind`. */
  code?: string | null;
  kind?: string | null;
  label?: Record<string, string> | string | null;
  retired: boolean;
};

export type ArticlePollType = {
  id: number;
  uid: string;
  archetype: string;
  source: 'extracted' | 'manual';
  extractorVersion: number;
  question: Record<string, string>;
  options: ArticlePollOptionRow[];
  nextOptionId: number;
  active: boolean;
  liveOptionsCount: number;
  totalVotes: number;
  /** Votos por id de opcion: es lo que permite decidir si retirar una. */
  tally: Record<string, number>;
  news: { id: number; slug: string; title: string };
  createdAt?: string;
  updatedAt?: string;
};
